import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { LuExternalLink } from "react-icons/lu";
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import __noop from '@atlaskit/ds-lib/noop';
import PageHeader from '@atlaskit/page-header';
import TextField from '@atlaskit/textfield';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Link } from 'react-router-dom';
import { supplyPartnerServices } from '@shared/api';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { setSupplyPartnerProfiles } from '@shared/store/partnerSlice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import {
    formatDateTime,
    renderCompletion,
    renderStatus
} from '@features/partnerManagement/components/partnerListCellHelpers';
import PartnerListTable from '@features/partnerManagement/components/PartnerListTable';
import type { SupplyPartnerProfile } from '@shared/api/services/partner.service';

const dialogOverlayStyles = {
    alignItems: 'center',
    background: 'var(--app-overlay)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    padding: token('space.400'),
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1000
} as const;

const dialogCardStyles = {
    background: token('color.background.input'),
    border: `1px solid ${token('color.border')}`,
    borderRadius: '18px',
    maxWidth: '520px',
    padding: token('space.300'),
    width: '100%'
} as const;

const dividerStyles = {
    background: token('color.border'),
    height: '1px',
    width: '100%'
} as const;

type AddSupplyPartnerDialogProps = {
    isOpen: boolean;
    isSaving: boolean;
    onClose: () => void;
    onSave: (spName: string) => void;
};

function AddSupplyPartnerDialog({
    isOpen,
    isSaving,
    onClose,
    onSave
}: AddSupplyPartnerDialogProps) {
    const [spName, setSpName] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSpName('');
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <Box style={dialogOverlayStyles}>
            <Box style={dialogCardStyles}>
                <Stack space="space.250">
                    <Inline spread="space-between" alignBlock="center">
                        <Text size="large" weight="medium">
                            Add Supply Partner
                        </Text>
                    </Inline>

                    <Box style={dividerStyles} />

                    <Stack space="space.100">
                        <Text color="color.text.subtle" size="small">
                            Supply Partner Name
                        </Text>
                        <TextField
                            aria-label="Supply partner name"
                            value={spName}
                            onChange={(event) => setSpName(event.currentTarget.value)}
                            placeholder="Enter supply partner name"
                        />
                    </Stack>

                    <Inline space="space.100">
                        <Button
                            appearance="primary"
                            isLoading={isSaving}
                            onClick={() => {
                                const trimmedName = spName.trim();

                                if (!trimmedName) {
                                    return;
                                }

                                onSave(trimmedName);
                            }}
                        >
                            Save
                        </Button>
                        <Button appearance="danger" isDisabled={isSaving} onClick={onClose}>
                            Cancel
                        </Button>
                    </Inline>
                </Stack>
            </Box>
        </Box>
    );
}

const supplyPartnerColumns: ColumnDef<SupplyPartnerProfile, unknown>[] = [
    {
        id: 'spId',
        header: 'SP ID',
        accessorKey: 'spId',
        sortingFn: 'alphanumeric',
        cell: ({ row }) => (
            <Link
                to={`/partner/sp/${row.original.spId}`}
                style={{
                    color: 'var(--ds-link, #0c66e4)',
                    fontWeight: 600,
                    textDecoration: 'none'
                }}
            >
                {row.original.spId}
            </Link>
        )
    },
    {
        id: 'spName',
        header: 'Name',
        accessorKey: 'spName',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'config',
        header: 'Config',
        cell: ({ row }) => (
            <Link
                to={`/partner/sp/${row.original.spId}/config`}
                style={{
                    color: 'var(--ds-link, #0c66e4)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    textDecoration: 'none'
                }}
            >
                View Config <LuExternalLink size={12} />
            </Link>
        )
    },
    {
        id: 'spSellerID',
        header: 'Seller ID',
        accessorKey: 'spSellerID',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => renderStatus(row.original.status)
    },
    {
        id: 'pocCompleted',
        header: 'POC',
        accessorKey: 'pocCompleted',
        cell: ({ row }) => renderCompletion(row.original.pocCompleted)
    },
    {
        id: 'paymentCompleted',
        header: 'Payment',
        accessorKey: 'paymentCompleted',
        cell: ({ row }) => renderCompletion(row.original.paymentCompleted)
    },
    {
        id: 'companyCompleted',
        header: 'Company',
        accessorKey: 'companyCompleted',
        cell: ({ row }) => renderCompletion(row.original.companyCompleted)
    },
    {
        id: 'createdBy',
        header: 'Created By',
        accessorKey: 'createdBy',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'createdAt',
        header: 'Created At',
        accessorKey: 'createdAt',
        cell: ({ row }) => formatDateTime(row.original.createdAt)
    },
    {
        id: 'updatedBy',
        header: 'Updated By',
        accessorKey: 'updatedBy',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'updatedAt',
        header: 'Updated At',
        accessorKey: 'updatedAt',
        cell: ({ row }) => formatDateTime(row.original.updatedAt)
    }
];

const SupplyPartner: () => JSX.Element = () => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const supplyPartners = useAppSelector((state) => Object.values(state.partner.supplyPartnerProfiles));
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const breadcrumbs = (
      <Breadcrumbs onExpand={__noop}>
        <BreadcrumbsItem text="Dashboard" key="Dashboard" href="/home" />
        <BreadcrumbsItem text="Supply Partner" key="Supply Partner" />
      </Breadcrumbs>
    );
    const { data } = useQuery({
        queryKey: ['allSpList'],
        queryFn: () => supplyPartnerServices.getAllSPprofileDetails()
    });

    const onboardMutation = useMutation({
        mutationFn: (spName: string) => supplyPartnerServices.onboardSp({ spName }),
        onSuccess: async () => {
            const nextSupplyPartners = await supplyPartnerServices.getAllSPprofileDetails();
            dispatch(setSupplyPartnerProfiles(nextSupplyPartners));
            void queryClient.invalidateQueries({ queryKey: ['allSpList'] });
            setIsAddDialogOpen(false);
        }
    });

    useEffect(() => {
        if (data) {
            dispatch(setSupplyPartnerProfiles(data));
        }
    }, [data, dispatch]);

    const actionsContent = (
        <ButtonGroup label="Content actions">
            <Button appearance="primary" onClick={() => setIsAddDialogOpen(true)}>
                Add supply partner
            </Button>
        </ButtonGroup>
    );

    return (
        <Stack space="space.300" xcss={{ width: '100%' }}>
            <PageHeader breadcrumbs={breadcrumbs} actions={actionsContent}>
                Supply Partner Management
            </PageHeader>
            <AddSupplyPartnerDialog
                isOpen={isAddDialogOpen}
                isSaving={onboardMutation.isPending}
                onClose={() => setIsAddDialogOpen(false)}
                onSave={(spName) => onboardMutation.mutate(spName)}
            />
            <PartnerListTable
                items={supplyPartners}
                columns={supplyPartnerColumns}
                emptyMessage="No supply partners found"
            />
        </Stack>)

};

export default SupplyPartner;
